"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  Polyline,
} from "@react-google-maps/api";

export default function LiveTrackingMap({
  driverLocation,
  customerLocation,
  path = [],
  onETAUpdate
}) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "",
  });

  const mapRef = useRef(null);

  const [BIKE_ICON, setBikeIcon] = useState(null);
  const [animatedDriverPos, setAnimatedDriverPos] = useState(driverLocation);

  const prevPos = useRef(driverLocation);

  // ✅ Load bike icon only after google is available
  useEffect(() => {
    if (!isLoaded || !window.google) return;

    setBikeIcon({
      url: "/images/bike_icon.png",
      scaledSize: new window.google.maps.Size(50, 50),
      anchor: new window.google.maps.Point(25, 25),
    });
  }, [isLoaded]);

  const animateMovement = (from, to, duration = 800) => {
    const start = performance.now();

    const step = (timestamp) => {
      const progress = Math.min((timestamp - start) / duration, 1);

      const newPos = {
        lat: from.lat + (to.lat - from.lat) * progress,
        lng: from.lng + (to.lng - from.lng) * progress,
      };

      setAnimatedDriverPos(newPos);

      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  };

  // ====== ETA CALCULATION ======
  useEffect(() => {
    if (!driverLocation || !customerLocation || !window.google) return;

    const service = new window.google.maps.DistanceMatrixService();

    service.getDistanceMatrix(
      {
        origins: [driverLocation],
        destinations: [customerLocation],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status !== "OK") return;
        if (response) {
          const result = response.rows[0].elements[0];
          const distance = result.distance?.text || "--";
          const duration = result.duration?.text || "--";

          if (onETAUpdate) onETAUpdate(duration, distance);
        }
      }
    );
  }, [driverLocation, customerLocation, isLoaded]);

  // ====== ANIMATE BIKE ======
  useEffect(() => {
    if (!driverLocation) return;

    if (
      prevPos.current &&
      prevPos.current.lat === driverLocation.lat &&
      prevPos.current.lng === driverLocation.lng
    ) {
      setAnimatedDriverPos(driverLocation);
      return;
    }

    const lastPos = prevPos.current;
    prevPos.current = driverLocation; // update AFTER

    if (lastPos) animateMovement(lastPos, driverLocation);
    else setAnimatedDriverPos(driverLocation);


    prevPos.current = driverLocation;
  }, [driverLocation]);

  const getHeading = (from, to) => {
    const lat1 = (from.lat * Math.PI) / 180;
    const lat2 = (to.lat * Math.PI) / 180;
    const dLng = ((to.lng - from.lng) * Math.PI) / 180;

    const y = Math.sin(dLng) * Math.cos(lat2);
    const x =
      Math.cos(lat1) * Math.sin(lat2) -
      Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

    const brng = (Math.atan2(y, x) * 180) / Math.PI;
    //console.log("Heading:", (brng + 360) % 360);
    return (brng + 360) % 360;
  };

  const heading =
    prevPos.current && animatedDriverPos
      ? getHeading(prevPos.current, animatedDriverPos)
      : 0;

  const onMapLoad = (map) => {
    mapRef.current = map;
    map.setZoom(17);
    map.setTilt(45);
  };

  const getRotatedImage = (url, angle, callback) => {
    const img = new Image();
    img.src = url;

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 60;

      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.translate(size / 2, size / 2);

      ctx.rotate(((angle + 180) * Math.PI) / 180);

      ctx.drawImage(img, -size / 2, -size / 2, size, size);

      callback(canvas.toDataURL());
    };
  };

  useEffect(() => {
    if (!animatedDriverPos) return;

    getRotatedImage("/images/bike_icon.png", heading, (rotated) => {
      setBikeIcon({
        url: rotated,
        scaledSize: new window.google.maps.Size(50, 50),
        anchor: new window.google.maps.Point(25, 25),
      });
    });
  }, [heading]);

  useEffect(() => {
    if (mapRef.current && animatedDriverPos) {
      mapRef.current.panTo(animatedDriverPos);
      mapRef.current.setZoom(18);
      mapRef.current.setTilt(45);
    }
  }, [animatedDriverPos]);

  if (!isLoaded) return <div>Loading map…</div>;

  return (
    <>
      <GoogleMap
        zoom={17}
        onLoad={onMapLoad}
        center={animatedDriverPos || customerLocation || { lat: 0, lng: 0 }}
        mapContainerStyle={{ width: "100%", height: "100%" }}
        options={{
          gestureHandling: "greedy",
          tilt: 45,
          mapId: "4504f8b37365c3d0",
        }}
      >
        {animatedDriverPos && BIKE_ICON && (
          <Marker
            position={animatedDriverPos}
            icon={{
              ...BIKE_ICON
            }}
          />
        )}

        {customerLocation && (
          <Marker
            position={customerLocation}
            icon={{
              url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
            }}
          />
        )}

        {path.length > 0 && (
          <Polyline
            path={path}
            options={{
              strokeColor: "#1E90FF",
              strokeOpacity: 0.9,
              strokeWeight: 5,
            }}
          />
        )}
      </GoogleMap>
    </>
  );
}
